import levenshtein from 'js-levenshtein';

import * as OpKinds from '../opKinds';
import * as Graph from '../graph';
import {nullableSkipTaggable} from '../opTypes';

export const opStringEqual = OpKinds.makeEqualOp({
  name: 'string-equal',
  argType: 'string',
});

export const opStringNotEqual = OpKinds.makeNotEqualOp({
  name: 'string-notEqual',
  argType: 'string',
});

const makeStringOp = OpKinds.makeStandardOp;

export const opStringIn = makeStringOp({
  hidden: true,
  name: 'string-in',
  argTypes: {
    lhs: `string`,
    rhs: {
      type: 'union',
      members: ['none', {type: 'list', objectType: 'string'}],
    },
  },
  description: `Checks if a ${Graph.docType('string')} is in a ${Graph.docType(
    'list'
  )} of ${Graph.docType('string', {plural: true})}`,
  argDescriptions: {
    lhs: `The ${Graph.docType('string')} to check`,
    rhs: `The ${Graph.docType('list')} of ${Graph.docType('string', {
      plural: true,
    })} to check against`,
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} is in the ${Graph.docType('list')}`,
  returnType: inputTypes =>
    nullableSkipTaggable(inputTypes.rhs, _ => 'boolean'),
  resolver: ({lhs, rhs}) => (rhs == null ? false : rhs.includes(lhs)),
});

export const opStringLen = makeStringOp({
  name: 'string-len',
  argTypes: {
    str: 'string',
  },
  description: `Returns the length of a ${Graph.docType('string')}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
  },
  returnValueDescription: `The length of the ${Graph.docType('string')}`,
  returnType: inputTypes => 'number',
  resolver: ({str}) => {
    // Don't use str.length because this does not correctly handle unicode
    return [...str].length;
  },
});

export const opStringAdd = OpKinds.makeBinaryStandardOp('+', {
  name: 'string-add',
  argTypes: {
    lhs: {type: 'union', members: ['none', 'string']},
    rhs: {type: 'union', members: ['none', 'string']},
  },
  description: `Concatenates two ${Graph.docType('string', {plural: true})}`,
  argDescriptions: {
    lhs: `The first ${Graph.docType('string')}`,
    rhs: `The second ${Graph.docType('string')}`,
  },
  returnValueDescription: `The concatenated ${Graph.docType('string')}`,
  returnType: inputTypes => 'string',
  resolver: ({lhs, rhs}) => {
    return '' + (lhs ?? '') + (rhs ?? '');
  },
});

export const opStringAppend = makeStringOp({
  name: 'string-append',
  argTypes: {
    str: 'string',
    suffix: 'string',
  },
  description: `Appends a suffix to a ${Graph.docType('string')}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to append to`,
    suffix: 'The suffix to append',
  },
  returnValueDescription: `The ${Graph.docType(
    'string'
  )} with the suffix appended`,
  returnType: inputTypes => 'string',
  resolver: ({str, suffix}) => str + suffix,
});
export const opStringPrepend = makeStringOp({
  name: 'string-prepend',
  argTypes: {
    str: 'string',
    prefix: 'string',
  },
  description: `Prepends a prefix to a ${Graph.docType('string')}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to prepend to`,
    prefix: 'The prefix to prepend',
  },
  returnValueDescription: `The ${Graph.docType(
    'string'
  )} with the prefix prepended`,
  returnType: inputTypes => 'string',
  resolver: ({str, prefix}) => '' + prefix + str,
});
export const opStringSplit = makeStringOp({
  name: 'string-split',
  argTypes: {
    str: 'string',
    sep: 'string',
  },
  description: `Splits a ${Graph.docType('string')} into a ${Graph.docType(
    'list'
  )} of ${Graph.docType('string', {plural: true})}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to split`,
    sep: 'The separator to split on',
  },
  returnValueDescription: `The ${Graph.docType('list')} of ${Graph.docType(
    'string',
    {plural: true}
  )}`,
  returnType: inputTypes => ({type: 'list', objectType: 'string'}),
  resolver: ({str, sep}) => str?.split(sep) ?? [],
});

export const opStringPartition = makeStringOp({
  name: 'string-partition',
  argTypes: {
    str: 'string',
    sep: 'string',
  },
  description: `Partitions a ${Graph.docType('string')} into a ${Graph.docType(
    'list'
  )} of the ${Graph.docType('string', {plural: true})}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to split`,
    sep: 'The separator to split on',
  },
  returnValueDescription: `A ${Graph.docType('list')} of ${Graph.docType(
    'string',
    {plural: true}
  )}: the ${Graph.docType(
    'string'
  )} before the separator, the separator, and the ${Graph.docType(
    'string'
  )} after the separator`,
  returnType: inputTypes => ({type: 'list', objectType: 'string'}),
  resolver: ({str, sep}) => {
    let result;
    const split = str.split(sep);
    if (split.length === 1) {
      result = [split[0], null, null];
    }
    result = [split[0], sep, split.slice(1).join(sep)];
    return result;
  },
});

// TODO(np): rPartition?

export const opStringStartsWith = makeStringOp({
  name: 'string-startsWith',
  argTypes: {
    str: 'string',
    prefix: {type: 'union', members: ['none', 'string']},
  },
  description: `Checks if a ${Graph.docType('string')} starts with a prefix`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
    prefix: 'The prefix to check for',
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} starts with the prefix`,
  returnType: inputTypes => 'boolean',
  resolver: ({str, prefix}) => (prefix ? str.startsWith(prefix) : true),
});

export const opStringEndsWith = makeStringOp({
  name: 'string-endsWith',
  argTypes: {
    str: 'string',
    suffix: {type: 'union', members: ['none', 'string']},
  },
  description: `Checks if a ${Graph.docType('string')} ends with a suffix`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
    suffix: 'The suffix to check for',
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} ends with the suffix`,
  returnType: inputTypes => 'boolean',
  resolver: ({str, suffix}) => (suffix ? str.endsWith(suffix) : true),
});

export const opStringIsAlpha = makeStringOp({
  name: 'string-isAlpha',
  argTypes: {
    str: 'string',
  },
  description: `Checks if a ${Graph.docType('string')} is alphabetic`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} is alphabetic`,
  returnType: inputTypes => 'boolean',
  resolver: ({str}) => str.match(/^[a-z]+$/i) !== null,
});

export const opStringIsNumeric = makeStringOp({
  name: 'string-isNumeric',
  argTypes: {
    str: 'string',
  },
  description: `Checks if a ${Graph.docType('string')} is numeric`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
  },
  returnValueDescription: `Whether the ${Graph.docType('string')} is numeric`,
  returnType: inputTypes => 'boolean',
  resolver: ({str}) => str.length > 0 && str.match(/^-?\d*(\.\d+)?$/i) !== null,
});

export const opStringIsAlnum = makeStringOp({
  name: 'string-isAlnum',
  argTypes: {
    str: 'string',
  },
  description: `Checks if a ${Graph.docType('string')} is alphanumeric`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} is alphanumeric`,
  returnType: inputTypes => 'boolean',
  resolver: ({str}) => str.match(/^[a-z0-9]+$/i) !== null,
});

// TODO(np): Other isX... isDigit, isUpper, isLower

export const opStringLower = makeStringOp({
  name: 'string-lower',
  argTypes: {
    str: 'string',
  },
  description: `Converts a ${Graph.docType('string')} to lowercase`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to convert to lowercase`,
  },
  returnValueDescription: `The lowercase ${Graph.docType('string')}`,
  returnType: inputTypes => 'string',
  resolver: ({str}) => str.toLocaleLowerCase(),
});
export const opStringUpper = makeStringOp({
  name: 'string-upper',
  argTypes: {
    str: 'string',
  },
  description: `Converts a ${Graph.docType('string')} to uppercase`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to convert to uppercase`,
  },
  returnValueDescription: `The uppercase ${Graph.docType('string')}`,
  returnType: inputTypes => 'string',
  resolver: ({str}) => str.toLocaleUpperCase(),
});

export const opStringSlice = makeStringOp({
  name: 'string-slice',
  argTypes: {
    str: 'string',
    begin: {type: 'union', members: ['none', 'number']},
    end: {type: 'union', members: ['none', 'number']},
  },
  description: `Slices a ${Graph.docType(
    'string'
  )} into a substring based on beginning and end indices`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to slice`,
    begin: 'The beginning index of the substring',
    end: 'The ending index of the substring',
  },
  returnValueDescription: `The substring`,
  returnType: inputTypes => 'string',
  resolver: ({str, begin, end}) => {
    if (begin == null && end != null) {
      return str.slice(0, end);
    }
    return str.slice(begin ?? undefined, end ?? undefined);
  },
});

export const opStringReplace = makeStringOp({
  name: 'string-replace',
  argTypes: {
    str: 'string',
    sub: 'string',
    newSub: 'string',
  },
  description: `Replaces all occurrences of a substring in a ${Graph.docType(
    'string'
  )}`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to replace contents of`,
    sub: 'The substring to replace',
    newSub: 'The substring to replace the old substring with',
  },
  returnValueDescription: `The ${Graph.docType(
    'string'
  )} with the replacements`,
  returnType: inputTypes => 'string',
  resolver: ({str, sub, newSub}) => str.replace(new RegExp(sub, 'g'), newSub),
});

export const opStringFindAll = makeStringOp({
  name: 'string-findAll',
  argTypes: {
    str: 'string',
    sub: 'string',
  },
  description: `Finds all occurrences of a substring in a ${Graph.docType(
    'string'
  )}`,
  argDescriptions: {
    str: `The ${Graph.docType(
      'string'
    )} to find occurrences of the substring in`,
    sub: 'The substring to find',
  },
  returnValueDescription: `The ${Graph.docType(
    'list'
  )} of indices of the substring in the ${Graph.docType('string')}`,
  returnType: inputTypes => ({type: 'list', objectType: 'string'}),
  resolver: ({str, sub}) => Array.from(str.match(new RegExp(sub, 'g')) || []),
});

export const opStringContains = makeStringOp({
  name: 'string-contains',
  argTypes: {
    str: 'string',
    sub: 'string',
  },
  description: `Checks if a ${Graph.docType('string')} contains a substring`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to check`,
    sub: 'The substring to check for',
  },
  returnValueDescription: `Whether the ${Graph.docType(
    'string'
  )} contains the substring`,
  returnType: inputTypes => 'boolean',
  resolver: ({str, sub}) => str.match(new RegExp(sub, 'g')) != null,
});
export const opStringStrip = makeStringOp({
  name: 'string-strip',
  argTypes: {
    str: 'string',
  },
  description: `Strip whitespace from both ends of a ${Graph.docType(
    'string'
  )}.`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to strip.`,
  },
  returnValueDescription: `The stripped ${Graph.docType('string')}.`,
  returnType: inputTypes => 'string',
  resolver: ({str}) => str.trim(),
});

export const opStringLeftStrip = makeStringOp({
  name: 'string-lStrip',
  argTypes: {
    str: 'string',
  },
  description: `Strip leading whitespace`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to strip.`,
  },
  returnValueDescription: `The stripped ${Graph.docType('string')}.`,
  returnType: inputTypes => 'string',
  resolver: ({str}) => str.trimStart(),
});

export const opStringRightStrip = makeStringOp({
  name: 'string-rStrip',
  argTypes: {
    str: 'string',
  },
  description: `Strip trailing whitespace`,
  argDescriptions: {
    str: `The ${Graph.docType('string')} to strip.`,
  },
  returnValueDescription: `The stripped ${Graph.docType('string')}.`,
  returnType: inputTypes => 'string',
  resolver: ({str}) => str.trimEnd(),
});

// Levenshtein distance is the minimum number of single-character
// edits it would take to go from one string to another
export const opStringLevenshtein = makeStringOp({
  name: 'string-levenshtein',
  renderInfo: {type: 'function'},
  argTypes: {
    str1: 'string',
    str2: 'string',
  },
  description: `Calculates the Levenshtein distance between two ${Graph.docType(
    'string',
    {plural: true}
  )}.`,
  argDescriptions: {
    str1: `The first ${Graph.docType('string')}.`,
    str2: `The second ${Graph.docType('string')}.`,
  },
  returnValueDescription: `The Levenshtein distance between the two ${Graph.docType(
    'string',
    {plural: true}
  )}.`,
  returnType: inputTypes => 'number',
  resolver: ({str1, str2}) => levenshtein(str1, str2),
});
